/**
 * Wraps a listener that a React effect attaches to `document` or `window`, so
 * it ignores the event that was already being dispatched when it was attached.
 *
 * Why: since React 17, React listens on the app's root element instead of on
 * `document`. If a click (or keypress) causes an effect to run while that event
 * is still being dispatched — which React 18 does synchronously for discrete
 * events under createRoot, and under the legacy root whenever the event causes
 * more than one render — the effect's new `document`/`window` listener then
 * receives that same event as it bubbles up. A "close when clicking outside"
 * listener closes what the click just opened; an Enter handler submits the
 * form the Enter just opened. With React 16 this could not happen for
 * `document` listeners: React's own listener was on `document`, and browsers
 * do not run listeners added to a target during that target's dispatch.
 *
 * `event.timeStamp` and `performance.now()` share a time origin, so any event
 * created before the listener existed is skipped. Every later event passes
 * through unchanged, in the same order as before.
 *
 * Keep the returned function to pass to `removeEventListener`:
 *
 *   useEffect(() => {
 *     const onClick = ignoreInFlightEvent((event) => { ... });
 *     document.addEventListener('click', onClick);
 *     return () => document.removeEventListener('click', onClick);
 *   }, [deps]);
 *
 * @param   {Function} listener  The event listener.
 * @returns {Function}           The listener, skipping the in-flight event.
 */
export const ignoreInFlightEvent = (listener) => {

	const attachedAt = performance.now();

	return (event) => {

		if (event.timeStamp < attachedAt) {
			return undefined;
		}

		return listener(event);

	};

};
